import { Business } from '@/api/models';
import { Button } from '@/components/ui/Button';
import { mdiCheck, mdiTrashCan } from '@mdi/js';
import * as React from 'react';

interface BusinessImagesTabProps {
  business: Business;
  onDeleteImage: (businessId: number, imageId: number) => void;
  onSetPrimaryImage: (businessId: number, imageId: number) => void;
  isDeletingImage: boolean;
  isSettingPrimaryImage: boolean;
}

export const BusinessImagesTab: React.FC<BusinessImagesTabProps> = ({
  business,
  onDeleteImage,
  onSetPrimaryImage,
  isDeletingImage,
  isSettingPrimaryImage,
}) => {
  const [confirmDeleteId, setConfirmDeleteId] = React.useState<number | null>(
    null,
  );

  const images = business.images || [];
  const canDelete = images.length > 1;

  const handleDeleteClick = (imageId: number) => {
    if (confirmDeleteId === imageId) {
      onDeleteImage(business.id!, imageId);
      setConfirmDeleteId(null);
    } else {
      setConfirmDeleteId(imageId);
    }
  };

  const handleSetPrimary = (imageId: number) => {
    onSetPrimaryImage(business.id!, imageId);
  };

  if (images.length === 0) {
    return (
      <div className='p-6 text-center text-grey-500'>
        No images available for this business.
      </div>
    );
  }

  return (
    <div className='p-6 space-y-4'>
      <div className='flex items-center justify-between mb-4'>
        <h4 className='text-sm font-medium text-grey-700'>
          Manage Images ({images.length})
        </h4>
        {!canDelete && (
          <p className='text-xs text-amber-600'>
            At least one image is required
          </p>
        )}
      </div>

      <div className='grid grid-cols-2 gap-4'>
        {images.map(img => {
          const imageId = img.image?.id;
          const isPrimary = img.primary;
          const isConfirmingDelete = confirmDeleteId === imageId;

          return (
            <div
              key={imageId}
              className={`relative rounded-lg overflow-hidden border-2 ${
                isPrimary ? 'border-blue-500' : 'border-grey-200'
              }`}
            >
              {/* Image */}
              <div className='aspect-video'>
                <img
                  src={img.image?.primaryUrl}
                  alt={`${business.name}`}
                  className='w-full h-full object-cover'
                />
              </div>

              {/* Primary Badge */}
              {isPrimary && (
                <div className='absolute top-2 left-2 bg-blue-500 text-white text-xs px-2 py-1 rounded-full font-medium'>
                  Primary
                </div>
              )}

              {/* Actions */}
              <div className='p-3 bg-white border-t border-grey-100'>
                <div className='flex flex-col gap-2'>
                  {/* Set Primary Button */}
                  {!isPrimary && (
                    <Button
                      icon={mdiCheck}
                      label='Set Primary'
                      color='secondary'
                      small
                      onClick={() => handleSetPrimary(imageId!)}
                      disabled={isSettingPrimaryImage || isDeletingImage}
                      loading={isSettingPrimaryImage}
                      className='w-full'
                    />
                  )}

                  {isPrimary && (
                    <span className='text-xs text-grey-500 text-center py-1'>
                      This is the main image
                    </span>
                  )}

                  {/* Delete Button */}
                  <Button
                    icon={mdiTrashCan}
                    label={isConfirmingDelete ? 'Confirm Delete' : 'Delete'}
                    color={isConfirmingDelete ? 'danger' : 'light'}
                    small
                    onClick={() => handleDeleteClick(imageId!)}
                    disabled={
                      !canDelete || isDeletingImage || isSettingPrimaryImage
                    }
                    loading={isDeletingImage && isConfirmingDelete}
                    title={
                      !canDelete
                        ? 'Cannot delete the last image'
                        : isConfirmingDelete
                          ? 'Click again to confirm deletion'
                          : 'Delete this image'
                    }
                    className='w-full'
                  />
                </div>

                {isConfirmingDelete && (
                  <p className='text-xs text-red-600 mt-2 text-center'>
                    Click again to confirm. This cannot be undone.
                  </p>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};
